// src/services/useAuthService.ts
import { useCallback, useState } from "react";
import useFetch, { ServiceParams, ServiceResponse, refreshSession } from "../hooks/useFetch";
import EndPoints from "../config/EndPoint";
import {
  broadcastAuthEvent,
  clearClientAuthState,
  clearLegacyAuthTokens,
  setSessionUser,
} from "./authSession";

export type Events = Partial<{
  onSuccess: (r: ServiceResponse) => void;
  onError: (r: ServiceResponse) => void;
}>;

/**
 * useAuthService
 * Auth con JWT en cookies HttpOnly (credentials). Sin tokens en Web Storage.
 */
const useAuthService = () => {
  const { doPost, doPut } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFetch = useCallback((key: string, val: boolean) => {
    setFetching((prev) => ({ ...prev, [key]: val }));
  }, []);

  /** LOGIN */
  const login = useCallback(
    async (email: string, password: string, events: Events = {}) => {
      const cleanEmail = email.trim().toLowerCase();
      const cleanPassword = password.trim();

      // Eliminar leftovers inseguros de sesiones anteriores.
      clearLegacyAuthTokens();

      const params: ServiceParams = {
        url: EndPoints.AUTH.LOGIN,
        body: { email: cleanEmail, password: cleanPassword },
        setFetching: (v) => setFetch("login", v),
        ...events,
      };

      const response = await doPost(params);

      if (!response?.http_status || response.http_status >= 400) {
        throw new Error(response?.message || "Error de autenticación");
      }

      const user = response.user || response.data?.user;
      if (user) {
        setSessionUser(user);
        broadcastAuthEvent("login");
      }

      return user;
    },
    [doPost, setFetch]
  );

  /** REFRESH — renueva cookies HttpOnly en el servidor */
  const refresh = useCallback(async () => {
    setFetch("refresh", true);
    try {
      const ok = await refreshSession();
      if (!ok) {
        clearClientAuthState();
        throw new Error("Sesión expirada");
      }
      return true;
    } finally {
      setFetch("refresh", false);
    }
  }, [setFetch]);

  /** CAMBIAR CONTRASEÑA (usuario autenticado) */
  const changePassword = useCallback(
    async (
      currentPassword: string,
      newPassword: string,
      events: Events = {}
    ) => {
      const params: ServiceParams = {
        url: EndPoints.AUTH.PASSWORD_CHANGE,
        body: {
          current_password: currentPassword.trim(),
          new_password: newPassword.trim(),
        },
        setFetching: (v) => setFetch("changePassword", v),
        ...events,
      };
      const response = await doPut(params);
      if (response?.http_status && response.http_status >= 400) {
        throw response;
      }
      return response;
    },
    [doPut, setFetch]
  );

  /** RESET DE CONTRASEÑA (solo admin) */
  const adminResetPassword = useCallback(
    async (userId: number, newPassword: string, events: Events = {}) => {
      const params: ServiceParams = {
        url: EndPoints.AUTH.PASSWORD_CHANGE,
        body: {
          user_id: userId,
          new_password: newPassword.trim(),
        },
        setFetching: (v) => setFetch("adminResetPassword", v),
        ...events,
      };
      const response = await doPut(params);
      if (response?.http_status && response.http_status >= 400) {
        throw response;
      }
      return response;
    },
    [doPut, setFetch]
  );

  /** LOGOUT — revoca refresh en backend (cookie) y limpia estado local */
  const logout = useCallback(async () => {
    try {
      await doPost({
        url: EndPoints.AUTH.LOGOUT,
        body: {},
        setFetching: (v) => setFetch("logout", v),
      } as ServiceParams);
    } catch {
      /* se limpia la sesión local de todas formas */
    }
    clearClientAuthState();
    broadcastAuthEvent("logout");
    window.location.href = "/login";
  }, [doPost, setFetch]);

  return { fetching, login, refresh, changePassword, adminResetPassword, logout };
};

export default useAuthService;
